// ---------------------------------------------------------------------------
// DesignSystemControls — 6-axis sidebar for /studio/design-system-edit
//
// Each axis renders one shadcn `<Select>`. The trigger displays the *label*
// (TitleCased) of the selected option rather than the raw value, so the panel
// reads "Lyra" not "lyra".
//
// History:
//   - Originally each axis was rendered as `<SelectValue />` with no formatter.
//     Base UI's <Select.Value> falls back to the raw `value` string until the
//     Portal-mounted SelectContent registers its items, which made every axis
//     show lowercase "lyra/neutral/default…" and a separate redundant legend
//     block at the bottom listed those same values a SECOND time. The user
//     reported this as "the right control panel is completely broken — shadcn
//     should be doing this." Both issues are fixed below.
// ---------------------------------------------------------------------------

import { useDesignSystem } from '../hooks/use-design-system'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import {
  STYLE_OPTIONS,
  BASE_COLOR_OPTIONS,
  THEME_OPTIONS,
  CHART_OPTIONS,
  RADIUS_OPTIONS,
  FONT_OPTIONS,
  type LabeledOption,
  type DesignSystemState,
} from '../lib/design-system-options'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@cogo/react-renderer'
import { AxisValueSwatch } from './axis-visuals'
import { radiusOptionLabel, type DesignAxesCopy } from '../source-derived/axes-copy'
import { SaveToLibraryDialog } from './save-to-library-dialog'

interface AxisDef {
  key: keyof DesignSystemState
  label: string
  options: LabeledOption[]
}

const AXES: AxisDef[] = [
  { key: 'style', label: 'Style', options: STYLE_OPTIONS },
  { key: 'baseColor', label: 'Base Color', options: BASE_COLOR_OPTIONS },
  { key: 'theme', label: 'Theme', options: THEME_OPTIONS },
  { key: 'chart', label: 'Chart', options: CHART_OPTIONS },
  { key: 'radius', label: 'Radius', options: RADIUS_OPTIONS },
  { key: 'font', label: 'Font', options: FONT_OPTIONS },
]

/** Build a value→label map once per axis so the trigger formatter is O(1). */
function buildLabelMap(options: LabeledOption[]): Record<string, string> {
  const m: Record<string, string> = {}
  for (const o of options) m[o.value] = o.label
  return m
}

const AXIS_LABEL_MAPS: Record<string, Record<string, string>> = AXES.reduce(
  (acc, axis) => {
    acc[axis.key as string] = buildLabelMap(axis.options)
    return acc
  },
  {} as Record<string, Record<string, string>>,
)

/**
 * The displayed label for one option of one axis.
 *
 * Radius is the only axis whose option labels are copy (`Default / None /
 * Small / Medium / Large` — size adjectives); every other axis lists proper
 * names (`Zinc`, `Noto Sans`) that stay Latin in every locale. Guarded on the
 * axis key rather than left to the lookup so a preset named `default` on some
 * other axis can never be renamed by the radius table.
 */
function optionLabel(
  axisKey: string,
  option: LabeledOption,
  copy: DesignAxesCopy,
): string {
  return axisKey === 'radius' ? radiusOptionLabel(option, copy) : option.label
}

export function DesignSystemControls() {
  const { designSystem, setOption } = useDesignSystem()
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.workbench
  // The axis names already exist in every locale for the source-derived
  // surface; reusing them keeps one axis vocabulary across both screens.
  const axesCopy = strings.designSystemAxes
  const axisLabels = axesCopy.axisLabels

  return (
    <aside className="flex min-h-0 w-full flex-col gap-4 self-stretch overflow-y-auto rounded-xl border border-border bg-card p-4 text-sm shadow-sm lg:w-72">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">
          {copy.controlsTitle}
        </h2>
      </div>

      <div className="flex flex-col gap-3">
        {AXES.map((axis) => {
          const labelMap = AXIS_LABEL_MAPS[axis.key as string]
          const currentValue = String(designSystem[axis.key])
          const axisLabel = axisLabels[axis.key] ?? axis.label
          return (
            <div key={axis.key as string} className="flex flex-col gap-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {axisLabel}
              </label>
              <Select
                value={currentValue}
                onValueChange={(v) => {
                  if (v !== null) setOption(axis.key, v)
                }}
              >
                <SelectTrigger className="h-10 rounded-lg border border-input bg-background px-3 text-xs font-medium text-foreground hover:bg-accent/50 focus:outline-none focus-visible:border-border focus-visible:ring-1 focus-visible:ring-border/60 focus-visible:ring-offset-0">
                  <SelectValue
                    placeholder={copy.selectPlaceholder.replace('{axis}', axisLabel)}
                  >
                    {/* The value's DESIGN leads; its name follows. */}
                    <span className="inline-flex items-center gap-2">
                      <AxisValueSwatch axis={axis.key as string} value={currentValue} />
                      {optionLabel(
                        axis.key as string,
                        {
                          value: currentValue,
                          label: labelMap[currentValue] ?? currentValue,
                        },
                        axesCopy,
                      )}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {axis.options.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="px-3 py-1.5 text-xs">
                      <span className="inline-flex items-center gap-2">
                        <AxisValueSwatch axis={axis.key as string} value={o.value} />
                        {optionLabel(axis.key as string, o, axesCopy)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {axis.key === 'baseColor' || axis.key === 'theme' ? (
                <div
                  className="flex flex-wrap items-center gap-1.5 pt-0.5"
                  data-testid={`axis-quick-pick-${axis.key}`}
                >
                  {axis.options.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      aria-label={`${axisLabel}: ${o.label}`}
                      aria-pressed={o.value === currentValue}
                      title={o.label}
                      onClick={() => setOption(axis.key, o.value)}
                      className={`rounded-full p-0.5 transition-shadow ${
                        o.value === currentValue
                          ? 'ring-2 ring-ring'
                          : 'hover:ring-2 hover:ring-border'
                      }`}
                    >
                      <AxisValueSwatch axis={axis.key as string} value={o.value} />
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-3">
        <SaveToLibraryDialog triggerClassName="flex h-10 items-center justify-center gap-2 rounded-lg bg-primary px-3 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors" />
      </div>
    </aside>
  )
}
